/** @format */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faFile,
  faTrash,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import CodeMirror from "@uiw/react-codemirror";
import { dracula } from "@uiw/codemirror-theme-dracula";
import { json } from "@codemirror/lang-json";
import { Modal, Spinner, Alert, Button, Card, Form } from "react-bootstrap";
import styles from "./ImportCSS.json";

export const StepBar = ({ step, role, t }) => {
  const steps = role === "superadmin"
    ? [
        t("Choose a model"),
        t("Choose a client"),
        t("Instance"),
        t("Documents"),
        t("Validation"),
        t("Finish"),
      ]
    : [
        t("Choose a model"),
        t("Instance"),
        t("Documents"),
        t("Validation"),
        t("Finish"),
      ];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: "30px",
      }}
    >
      {steps.map((label, idx) => {
        const isActive = step === idx;
        const isDone = step > idx;

        return (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              margin: "5px",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: isActive
                  ? "#3c6382"
                  : isDone
                  ? "#78e08f"
                  : "#d1d8e0",
                color: isActive || isDone ? "#fff" : "#636e72",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: 16,
                border: isActive
                  ? "2px solid #3c6382"
                  : "2px solid #d1d8e0",
                transition: "background 0.3s",
              }}
            >
              {idx + 1}
            </div>

            <div
              style={{
                marginLeft: 8,
                marginRight: 8,
                color: isActive ? "#3c6382" : "#636e72",
                fontWeight: isActive ? "bold" : "normal",
                fontSize: 14,
                maxWidth: 120,
                textAlign: "center",
                wordWrap: "break-word",
              }}
            >
              {label}
            </div>

            {idx < steps.length - 1 && <div style={{ width: 32, height: 2 }} />}
          </div>
        );
      })}
    </div>
  );
};

// Modal de chargement
export const LoadingModal = ({ loading, t }) => (
  <Modal show={loading} centered backdrop="static">
    <Modal.Body className="text-center">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">{t("Loading")}...</span>
      </Spinner>
      <h4>{t("Processing in progress")}...</h4>
    </Modal.Body>
  </Modal>
);

// Message d'erreur
const ErrorAlert = ({ error, t }) => (
  <Alert
    variant="danger"
    style={{
      width: "90%",
      maxWidth: "600px",
      margin: "20px auto 0 auto",
      padding: "2%",
    }}
  >
    {error}
  </Alert>
);

// Message d'erreur lors du traitement
export const StepError = ({ error, t, setStep, finalRoute, navigate }) => (
  <>
    <Card
      className="text-center"
      style={{
        ...styles.CardStyles,
        width: "90%",
        maxWidth: "600px",
        margin: "0 auto",
      }}
    >
      <Card.Body>
        {t("Error in processing, please try again")}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px",
            marginTop: 16,
          }}
        >
          <Button className="btn-success" onClick={() => navigate(finalRoute)}>
            {t("Return instance")}
          </Button>
          <Button className="btn-success" onClick={() => setStep(0)}>
            {t("Add instance")}
          </Button>
        </div>
      </Card.Body>
    </Card>
    {error && <ErrorAlert error={error} t={t} />}
  </>
);

// Étape 0 : Choix du modèle
export const Step0Model = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { modelList, selectedModel, error } = state;
  const { handleModelChange, handleNext } = handlers;
  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: "600px",
          margin: "0 auto",
          height: "auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Choose a model")}
          </Card.Title>
          <Form.Select
            className="FormSelect"
            value={selectedModel}
            onChange={handleModelChange}
            style={{
              width: "100%",
              maxWidth: "400px",
              margin: "0 auto 10px auto",
              display: "block",
            }}
          >
            <option value="All">{t("Choice of model")}</option>
            {modelList.map((model) => (
              <option key={model.ModelId} value={model.ModelId}>
                {model.ModelName}
              </option>
            ))}
          </Form.Select>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginTop: 16,
            }}
          >
            <Button
              className="btn"
              style={styles.NextButton}
              onClick={handleNext}
            >
              {t("Next")}{" "}
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 10 }} />
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// Étape 1 : Choix du client (pour superadmin)
export const Step1Client = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { clientList, selectedClient, error } = state;
  const { handleClientChange, handleNext, setStep } = handlers;
  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: "600px",
          margin: "0 auto",
          height: "auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Choose a client")}
          </Card.Title>
          <Form.Select
            className="FormSelect"
            value={selectedClient}
            onChange={handleClientChange}
            style={{
              width: "100%",
              maxWidth: "400px",
              margin: "0 auto 10px auto",
              display: "block",
            }}
          >
            <option value="All">{t("Choice of client")}</option>
            {clientList.map((client) => (
              <option key={client.ClientId} value={client.ClientId}>
                {client.ClientId}
              </option>
            ))}
          </Form.Select>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginTop: 16,
            }}
          >
            <Button
              className="btn btn-secondary"
              onClick={() => setStep(0)}
            >
              {t("Previous")}
            </Button>
            <Button
              className="btn"
              style={styles.NextButton}
              onClick={handleNext}
            >
              {t("Next")}{" "}
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 10 }} />
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// Étape 2 : Configuration de l'instance
export const Step2Instance = ({ state, handlers, computed }) => {
  const { t } = computed;
  const {
    instanceName,
    iExpectedDocs,
    useExistingInstance,
    existingInstanceName,
    instanceList,
    error,
    role,
  } = state;
  const {
    setUseExistingInstance,
    setInstanceName,
    setIExpectedDocs,
    handleExistingInstanceChange,
    handleNext,
    setStep,
    setDroppedFileNames,
  } = handlers;
  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: "600px",
          margin: "0 auto",
          height: "auto",
        }}
      >
        <Card.Body>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            <Button
              className={`btn ${
                !useExistingInstance ? "btn-success" : "btn-light"
              }`}
              onClick={() => {
                setUseExistingInstance(false);
                setDroppedFileNames([]);
              }}
            >
              {t("New instance")}
            </Button>
            <Button
              className={`btn ${
                useExistingInstance ? "btn-success" : "btn-light"
              }`}
              onClick={() => setUseExistingInstance(true)}
            >
              {t("Existing instance")}
            </Button>
          </div>

          {!useExistingInstance ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Form.Control
                style={{
                  ...styles.FormControl,
                  width: "100%",
                  maxWidth: "400px",
                }}
                type="text"
                placeholder={t("Enter the instance name")}
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
              />
              <Form.Control
                style={{
                  ...styles.FormControl,
                  width: "100%",
                  maxWidth: "400px",
                  marginTop: 5,
                  marginBottom: 10,
                }}
                type="number"
                value={iExpectedDocs === 0 ? "" : iExpectedDocs}
                min="0"
                placeholder={t("Enter the number of documents expected")}
                onChange={(e) => setIExpectedDocs(Number(e.target.value) || 0)}
              />
            </div>
          ) : (
            <Form.Select
              style={{
                width: "100%",
                maxWidth: "500px",
                margin: "0 auto 10px auto",
                display: "block",
              }}
              value={existingInstanceName}
              onChange={handleExistingInstanceChange}
            >
              <option value="">
                {t("Select an existing instance")}
              </option>
              {instanceList
                .filter(
                  (instance) =>
                    instance.iExpectedDocs > (instance.iCreatedDocs || 0)
                )
                .map((instance) => (
                  <option
                    key={instance.kInstanceId}
                    value={instance.kInstanceId}
                  >
                    {instance.kInstanceId} /{" "}
                    {instance.sInstanceName}
                  </option>
                ))}
            </Form.Select>
          )}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginTop: 16,
            }}
          >
            <Button
              className="btn btn-secondary"
              onClick={() => setStep(role === "superadmin" ? 1 : 0)}
            >
              {t("Previous")}
            </Button>
            <Button
              className="btn"
              style={styles.NextButton}
              onClick={handleNext}
            >
              {t("Next")}{" "}
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 10 }} />
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// Étape 3 : Client final
export const Step3FinalCustomer = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { sFinalCustomer, error } = state;
  const { setSFinalCustomer, handleNext, setStep } = handlers;

  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: "600px",
          margin: "0 auto",
          height: "auto",
        }}
      >
        <Card.Body>
          <Card.Title
            style={{ color: "#3c6382", marginBottom: 10 }}
          >
            {t("Enter the end customer name")}
          </Card.Title>
          <Form.Control
            style={{
              ...styles.FormControl,
              width: "100%",
              maxWidth: "400px",
              margin: "0 auto 10px auto",
              display: "block",
            }}
            type="text"
            placeholder={t("Enter the end customer name")}
            value={sFinalCustomer}
            onChange={(e) => setSFinalCustomer(e.target.value)}
          />

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginTop: 16,
            }}
          >
            <Button
              className="btn btn-secondary"
              onClick={() => setStep(2)}
            >
              {t("Previous")}
            </Button>
            <Button
              className="btn"
              style={styles.NextButton}
              onClick={handleNext}
            >
              {t("Next")}{" "}
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 10 }} />
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// Étape 4 : Documents  (maintenant index 3)
export const Step4Documents = ({ state, handlers, computed }) => {
  const { t } = computed;
  const {
    error,
    droppedFileNames,
    fileJSONs,
    isAutoFilled,
    iExpectedDocs,
    role,
  } = state;
  const {
    handleDragOver,
    handleDrop,
    handleFileSelect,
    handleFileDelete,
    handleFillAllJSON,
    handleResetJSONs,
    handleOpenJSONEditor,
    handleRemoveJSON,
    handleNext,
    setStep,
    getSupportedFileTypes,
    getRemainingSlots,
  } = { ...handlers, ...computed };

  const remainingSlots = getRemainingSlots(); // Calculé mais utilisé uniquement pour `disabled`

  const filteredFileNames = droppedFileNames.filter((fileName) => fileName !== t("doc already created"));

  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: "600px",
          margin: "0 auto",
          height: "auto",
        }}
        onDragOver={(event) => {
          if (remainingSlots > 0) handleDragOver(event);
        }}
        onDrop={handleDrop}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("AddFiles")}
          </Card.Title>
          <p>
            {t("FileChose")} {getSupportedFileTypes().join(", ")}
          </p>
          <div
            style={{
              color: "#3c6382",
              marginBottom: "10px",
              maxHeight: "80px",
              overflowY: "auto",
              border: "1px solid #ccc",
              padding: "10px",
            }}
          >
            {droppedFileNames.length > 1 && (
              <>
                <Button
                  className="btn"
                  onClick={handleFillAllJSON}
                  disabled={fileJSONs.every(
                    (json) => json.trim() !== ""
                  )}
                  style={{ marginBottom: 10 }}
                >
                  {t("Fill All JSON")}
                </Button>
                {isAutoFilled && (
                  <button
                    onClick={handleResetJSONs}
                    style={{
                      marginLeft: 10,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </>
            )}

            <ul>
              {filteredFileNames.map((fileName, index) => (
                <li key={`${fileName}-${index}`}>
                  {fileName}
                  <Button
                    onClick={() => handleOpenJSONEditor(index + droppedFileNames.length - filteredFileNames.length)} 
                    disabled={fileJSONs.every((json) => json.trim() !== "")}
                    style={{
                      marginLeft: 10,
                      backgroundColor:
                        fileJSONs[index + droppedFileNames.length - filteredFileNames.length]?.trim() !== ""
                          ? "red"
                          : "",
                      borderColor:
                        fileJSONs[index + droppedFileNames.length - filteredFileNames.length]?.trim() !== ""
                          ? "red"
                          : "",
                    }}
                  >
                    JSON
                  </Button>
                  {fileJSONs[index + droppedFileNames.length - filteredFileNames.length]?.trim() !== "" && (
                    <button
                      onClick={() => handleRemoveJSON(index + droppedFileNames.length - filteredFileNames.length)}
                      style={{
                        marginLeft: 5,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "red",
                      }}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <p>
            {t("Files drop")}: {droppedFileNames.length} /{" "}
            {t("Documents expected")}: {iExpectedDocs}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <Button
              className="btn btn-primary"
              onClick={handleFileSelect}
              disabled={remainingSlots <= 0}
            >
              <FontAwesomeIcon icon={faFile} /> {t("File choice")}
            </Button>
            <Button
              className="btn btn-danger"
              onClick={handleFileDelete}
            >
              <FontAwesomeIcon icon={faTrash} /> {t("Delete")}
            </Button>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginTop: 16,
            }}
          >
            <Button
              className="btn btn-secondary"
              onClick={() => setStep(role === "superadmin" ? 2 : 1)}
            >
              {t("Previous")}
            </Button>
            <Button
              className="btn"
              style={styles.NextButton}
              onClick={handleNext}
            >
              {t("Next")}{" "}
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 10 }} />
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// Étape 5 : Validation (nouveau index 4 pour superadmin, 3 pour non)
export const Step5Validation = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { error, role } = state;
  const { handleFileSubmit, setStep } = handlers;
  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: "600px",
          margin: "0 auto",
          height: "auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Validate instance creation")}
          </Card.Title>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginTop: 16,
            }}
          >
            <Button
              className="btn btn-secondary"
              onClick={() => setStep(role === "superadmin" ? 3 : 2)}
            >
              {t("Previous")}
            </Button>
            <Button
              className="btn btn-success"
              onClick={handleFileSubmit}
            >
              <FontAwesomeIcon icon={faCheck} /> {t("Up")}
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// Étape 6 : Succès
export const Step6Success = ({ state, computed, navigate, handlers }) => {
  const { t, finalRoute } = computed;
  const { role } = state;
  const { setStep } = handlers;

  return (
    <Alert
      variant="success"
      style={{
        width: "90%",
        maxWidth: "550px",
        margin: "20px auto",
      }}
    >
      {t("Treatment completed successfully")}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "10px",
          marginTop: 10,
        }}
      >
        <Button className="btn-success" onClick={() => navigate(finalRoute)}>
          {role === "superadmin" ? t("Return") : t("Return instance")}
        </Button>
        <Button className="btn-success" onClick={() => setStep(0)}>
          {t("Add instance")}
        </Button>
      </div>
    </Alert>
  );
};


// Modal de l'éditeur JSON
export const JSONEditorModal = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { showJSONEditor, value, error } = state;
  const { handleCloseJSONEditor, onChangeJSON } = handlers;

  return (
    <Modal show={showJSONEditor} onHide={handleCloseJSONEditor} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{t("Editor JSON")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CodeMirror
          value={value}
          height="200px"
          theme={dracula}
          extensions={[json({ jsx: true })]}
          onChange={onChangeJSON}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleCloseJSONEditor}>{t("Apply")}</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Modal pour remplir tous les JSON
export const FillAllJSONModal = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { showFillAllModal, defaultJSONValue, error } = state;
  const { setDefaultJSONValue, setShowFillAllModal, handleApplyAllJSON } = handlers;

  return (
    <Modal show={showFillAllModal} onHide={() => setShowFillAllModal(false)} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{t("All JSON editor")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CodeMirror
          value={defaultJSONValue}
          height="200px"
          theme={dracula}
          extensions={[json({ jsx: true })]}
          onChange={setDefaultJSONValue}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleApplyAllJSON}
        >
          {t("Apply at all")}
        </Button>
      </Modal.Footer>
      {error && (
        <Alert variant="danger" style={{ marginLeft: "5%", width: "90%" }}>
          {error}
        </Alert>
      )}
    </Modal>
  );
};